import React, { useEffect, useRef, useState } from 'react';

/** Same convention as the rest of the site: relative in production, local API server in dev. */
export const getApiUrl = (endpoint) =>
  process.env.NODE_ENV === 'production' ? endpoint : `http://localhost:5000${endpoint}`;

export const WHATSAPP_NUMBER = '919587773274';

export const whatsappLink = (text) => `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(text)}`;

export const prefersReducedMotion = () =>
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/** Smooth-scrolls to an in-page section (instant for reduced motion) and moves focus there. */
export function scrollToSection(id) {
  const el = document.getElementById(id);
  if (!el) return false;
  el.scrollIntoView({ behavior: prefersReducedMotion() ? 'auto' : 'smooth', block: 'start' });
  if (!el.hasAttribute('tabindex')) el.setAttribute('tabindex', '-1');
  el.focus({ preventScroll: true });
  return true;
}

/** Fetches JSON once on mount. Returns { data, loading, error }. */
export function useJson(endpoint) {
  const [state, setState] = useState({ data: null, loading: true, error: false });
  useEffect(() => {
    let alive = true;
    fetch(getApiUrl(endpoint))
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json();
      })
      .then((data) => alive && setState({ data, loading: false, error: false }))
      .catch(() => alive && setState({ data: null, loading: false, error: true }));
    return () => {
      alive = false;
    };
  }, [endpoint]);
  return state;
}

/** True once the element has come within `rootMargin` of the viewport (sticky). */
export function useNearViewport(rootMargin = '600px 0px') {
  const ref = useRef(null);
  const [near, setNear] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el || near) return undefined;
    if (typeof IntersectionObserver === 'undefined') {
      setNear(true);
      return undefined;
    }
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setNear(true);
          io.disconnect();
        }
      },
      { rootMargin }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [near, rootMargin]);
  return [ref, near];
}

/** Tracks a media query. */
export function useMediaQuery(query) {
  const get = () => typeof window !== 'undefined' && typeof window.matchMedia === 'function' && window.matchMedia(query).matches;
  const [matches, setMatches] = useState(get);
  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return undefined;
    const mql = window.matchMedia(query);
    const onChange = () => setMatches(mql.matches);
    onChange();
    mql.addEventListener ? mql.addEventListener('change', onChange) : mql.addListener(onChange);
    return () => (mql.removeEventListener ? mql.removeEventListener('change', onChange) : mql.removeListener(onChange));
  }, [query]);
  return matches;
}

export const getInitials = (name = '') =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0].toUpperCase())
    .join('');

/** Small uppercase label above section headings. */
export function Eyebrow({ children, className = '' }) {
  return (
    <p className={`inline-flex items-center gap-2 text-xs sm:text-sm font-semibold uppercase tracking-[0.18em] text-apn-orange ${className}`}>
      <span aria-hidden="true" className="h-2 w-2 rounded-full bg-apn-orange" />
      {children}
    </p>
  );
}

export const focusRing =
  'focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-apn-orange focus-visible:ring-offset-2 focus-visible:ring-offset-apn-bg';

export function WhatsAppIcon({ className = 'h-5 w-5' }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M17.47 14.38c-.3-.15-1.76-.87-2.03-.97-.27-.1-.47-.15-.67.15-.2.3-.77.97-.94 1.16-.17.2-.35.22-.64.08-.3-.15-1.26-.47-2.39-1.48-.88-.79-1.48-1.76-1.65-2.06-.17-.3-.02-.46.13-.6.13-.14.3-.35.45-.52.15-.18.2-.3.3-.5.1-.2.05-.37-.03-.52-.07-.15-.67-1.61-.92-2.2-.24-.58-.49-.5-.67-.51h-.57c-.2 0-.52.08-.8.37-.27.3-1.04 1.02-1.04 2.48s1.07 2.88 1.21 3.07c.15.2 2.1 3.2 5.08 4.49.71.3 1.26.49 1.7.63.71.22 1.36.2 1.87.12.57-.09 1.76-.72 2-1.41.25-.7.25-1.29.18-1.41-.08-.13-.27-.2-.57-.35M12.05 21.78h-.01a9.87 9.87 0 0 1-5.03-1.38l-.36-.21-3.74.98 1-3.65-.24-.37a9.86 9.86 0 0 1-1.51-5.26c0-5.45 4.44-9.88 9.89-9.88 2.64 0 5.12 1.03 6.99 2.9a9.83 9.83 0 0 1 2.89 6.99c0 5.45-4.44 9.88-9.88 9.88m8.41-18.3A11.82 11.82 0 0 0 12.05 0C5.5 0 .16 5.34.16 11.89c0 2.1.55 4.14 1.59 5.95L.06 24l6.3-1.65a11.88 11.88 0 0 0 5.68 1.45h.01c6.55 0 11.89-5.34 11.89-11.89 0-3.18-1.24-6.16-3.49-8.41" />
    </svg>
  );
}

export function ArrowIcon({ className = 'h-5 w-5', direction = 'right' }) {
  const rotate = { right: '', left: 'rotate-180', down: 'rotate-90', up: '-rotate-90' }[direction];
  return (
    <svg className={`${className} ${rotate}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M5 12h14M13 6l6 6-6 6" />
    </svg>
  );
}

export function SoundIcon({ on, className = 'h-4 w-4' }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M11 5 6 9H2v6h4l5 4V5z" fill="currentColor" />
      {on ? (
        <path d="M15.5 8.5a5 5 0 0 1 0 7M18.5 5.5a9 9 0 0 1 0 13" />
      ) : (
        <path d="m22 9-6 6M16 9l6 6" />
      )}
    </svg>
  );
}

function playVideo(v) {
  const p = v.play();
  if (p && typeof p.catch === 'function') p.catch(() => {});
}

/**
 * A vertical reel: muted by default, plays only while on screen (never with reduced motion unless
 * the viewer taps it), and tapping it turns its sound on. Only one reel per group has sound:
 * the parent owns `soundOn` and `onToggleSound`.
 */
export function ReelTile({ src, poster, title, soundOn, onToggleSound, className = '' }) {
  const videoRef = useRef(null);
  const wrapRef = useRef(null);

  useEffect(() => {
    const v = videoRef.current;
    const el = wrapRef.current;
    if (!v || !el || typeof IntersectionObserver === 'undefined') return undefined;
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          if (!prefersReducedMotion() || !v.muted) playVideo(v);
        } else {
          v.pause();
        }
      },
      { threshold: 0.2 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [src]);

  // React doesn't keep the `muted` property in sync, so set it directly.
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = !soundOn;
    if (soundOn) playVideo(v);
    else if (prefersReducedMotion()) v.pause();
  }, [soundOn]);

  return (
    <div ref={wrapRef} className={`relative overflow-hidden bg-apn-surface-2 ${className}`}>
      <video
        ref={videoRef}
        src={src}
        poster={poster}
        muted
        loop
        playsInline
        preload="none"
        aria-hidden="true"
        tabIndex={-1}
        className="absolute inset-0 h-full w-full object-cover"
      />
      <button
        type="button"
        onClick={onToggleSound}
        aria-pressed={soundOn}
        aria-label={`${soundOn ? 'Mute' : 'Play with sound'}: ${title || 'client reel'}`}
        className={`group absolute inset-0 flex items-end justify-end p-2 sm:p-3 ${focusRing} focus-visible:ring-inset`}
      >
        <span
          className={`flex h-11 w-11 items-center justify-center rounded-full backdrop-blur-md transition ${
            soundOn ? 'bg-apn-orange text-white' : 'bg-black/45 text-white group-hover:bg-black/65'
          }`}
        >
          <SoundIcon on={soundOn} />
        </span>
      </button>
    </div>
  );
}
